import { uniqBy } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'

import { type PreviewDocument, previewDocumentKey } from './previewDocument'

/**
 * The documents shown beside an agent chat: the forms the agent filled, then
 * the chips the user opened. Hand setFilledDocuments and pickDocument to
 * ChatPanel as onFilledDocuments and onPreviewDocument. Both reset when the
 * conversation changes.
 */
export function useDocumentPreview(conversationId: string | undefined) {
  // Split so a chip the user opened isn't dropped the next time the agent fills something.
  const [filledDocuments, setFilledDocuments] = useState<PreviewDocument[]>([])
  const [pickedDocuments, setPickedDocuments] = useState<PreviewDocument[]>([])

  // Reset during render, not in an effect: ChatPanel's effects refill the new
  // conversation's documents in the same commit, and a parent effect would run
  // after them and wipe those out.
  const [shownConversationId, setShownConversationId] = useState(conversationId)
  if (shownConversationId !== conversationId) {
    setShownConversationId(conversationId)
    setFilledDocuments([])
    setPickedDocuments([])
  }

  const previewDocuments = useMemo(
    () => uniqBy([...filledDocuments, ...pickedDocuments], previewDocumentKey),
    [filledDocuments, pickedDocuments]
  )

  const closePreview = useCallback(() => {
    setFilledDocuments([])
    setPickedDocuments([])
  }, [])

  const pickDocument = useCallback((doc: PreviewDocument) => setPickedDocuments((prev) => [...prev, doc]), [])

  return { previewDocuments, setFilledDocuments, pickDocument, closePreview }
}
